<script setup lang="ts">
import type * as DotLottieNameSpace from '@lottiefiles/dotlottie-web';
import { computed, ref, watch } from 'vue';

import { useWangsVueConfig } from 'lib/plugins/WangsVue';
import mergePropsWithDefaults from 'lib/utils/mergePropsWithDefaults.util';

import { AnimationProps } from './Animation.vue.d';
import useDarkStore from '../toggledarkmode/store';

const props = withDefaults(defineProps<AnimationProps>(), {
  loop: true,
  autoplay: true,
});

const { defaultProps } = useWangsVueConfig('Animation');
const { darkMode } = useDarkStore();
const config = computed(() => mergePropsWithDefaults(props, defaultProps));

const canvasRef = ref<HTMLCanvasElement | null>(null);

const importAnimation = async (): Promise<void> => {
  let module;
  try {
    module = await import(
      `../../assets/lottie/${config.value.libPath}/${props.animation}${darkMode.value ? '-dark' : ''}.lottie`
    );
  } catch {
    module = await import(
      `../../assets/lottie/${config.value.libPath}/${props.animation}.lottie`
    );
  }

  const { DotLottie } = await importDotLottie();

  if (canvasRef.value) {
    new DotLottie({
      canvas: canvasRef.value,
      src: module.default,
      autoplay: props.autoplay,
      loop: props.loop,
      ...props.customConfig,
    });
  }
};

const importDotLottie = (): Promise<typeof DotLottieNameSpace> => {
  if ('System' in window) {
    // For single spa with SystemJS
    return new Function(
      'return import("https://cdn.jsdelivr.net/npm/@lottiefiles/dotlottie-web@0.38.2/+esm")',
    )();
  }

  // For microtsm with native ESM support
  return import(
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    'https://cdn.jsdelivr.net/npm/@lottiefiles/dotlottie-web@0.38.2/+esm'
  );
};

watch(darkMode, importAnimation, { immediate: true });
</script>

<template>
  <div data-wv-name="animation" v-bind="$attrs">
    <canvas ref="canvasRef" style="width: 100%; height: 100%" />
  </div>
</template>
